/**
 * Export Page - T188
 *
 * Page for exporting data (DATEV, CSV)
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, Select } from '../components/ui';
import { useToastHelpers } from '../components/ui';

export const Route = createFileRoute('/export')({
  component: ExportPage,
});

function ExportPage() {
  const toast = useToastHelpers();

  // Date range state
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Default to current month
  const [startDate, setStartDate] = useState(
    `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`
  );
  const [endDate, setEndDate] = useState(
    new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0]
  );
  const [invoiceStatus, setInvoiceStatus] = useState<'all' | 'paid' | 'sent'>('all');

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  };

  // Quick date range presets
  const setCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  const setLastMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  const setCurrentQuarter = () => {
    const today = new Date();
    const quarter = Math.floor(today.getMonth() / 3);
    const firstDay = new Date(today.getFullYear(), quarter * 3, 1);
    const lastDay = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  };

  const setCurrentYear = () => {
    const today = new Date();
    setStartDate(`${today.getFullYear()}-01-01`);
    setEndDate(`${today.getFullYear()}-12-31`);
  };

  // Download handlers
  const handleDatevExport = () => {
    const url = `${getApiBaseUrl()}/export/datev?startDate=${startDate}&endDate=${endDate}&status=${invoiceStatus}`;
    window.open(url, '_blank');
    toast.success('DATEV-Export gestartet');
  };

  const handleInvoicesCsvExport = () => {
    let url = `${getApiBaseUrl()}/export/csv/invoices?`;
    if (startDate && endDate) {
      url += `startDate=${startDate}&endDate=${endDate}&`;
    }
    if (invoiceStatus !== 'all') {
      url += `status=${invoiceStatus}`;
    }
    window.open(url, '_blank');
    toast.success('Rechnungen-Export gestartet');
  };

  const handleCustomersCsvExport = () => {
    const url = `${getApiBaseUrl()}/export/csv/customers`;
    window.open(url, '_blank');
    toast.success('Kunden-Export gestartet');
  };

  const handleEstimatesCsvExport = () => {
    let url = `${getApiBaseUrl()}/export/csv/estimates?`;
    if (startDate && endDate) {
      url += `startDate=${startDate}&endDate=${endDate}`;
    }
    window.open(url, '_blank');
    toast.success('Angebote-Export gestartet');
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-white">Datenexport</h1>
          <p className="text-white/50 mt-1">
            Exportieren Sie Ihre Daten für Ihren Steuerberater oder zur Archivierung
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Date Range Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Period Selection */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Zeitraum</h2>

              {/* Quick presets */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button type="button" variant="secondary" size="sm" onClick={setCurrentMonth}>
                  Dieser Monat
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={setLastMonth}>
                  Letzter Monat
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={setCurrentQuarter}>
                  Dieses Quartal
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={setCurrentYear}>
                  Dieses Jahr
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1">Von</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1">Bis</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm text-white/60 mb-1">Rechnungsstatus</label>
                <Select
                  value={invoiceStatus}
                  onChange={(e) => setInvoiceStatus(e.target.value as 'all' | 'paid' | 'sent')}
                  options={[
                    { value: 'all', label: 'Alle (außer Entwürfe)' },
                    { value: 'sent', label: 'Nur versendete' },
                    { value: 'paid', label: 'Nur bezahlte' },
                  ]}
                />
              </div>
            </GlassPanel>

            {/* DATEV Export */}
            <GlassPanel>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-medium text-white">DATEV-Export</h2>
                  <p className="text-sm text-white/50 mt-1">
                    Exportieren Sie Ihre Rechnungen im DATEV-Format für Ihren Steuerberater
                  </p>
                </div>
                <Button onClick={handleDatevExport}>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  DATEV herunterladen
                </Button>
              </div>
            </GlassPanel>

            {/* CSV Exports */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">CSV-Exporte</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white">Rechnungen</p>
                    <p className="text-sm text-white/50">Alle Rechnungen im ausgewählten Zeitraum</p>
                  </div>
                  <Button variant="secondary" onClick={handleInvoicesCsvExport}>
                    Herunterladen
                  </Button>
                </div>

                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white">Angebote</p>
                    <p className="text-sm text-white/50">Alle Angebote im ausgewählten Zeitraum</p>
                  </div>
                  <Button variant="secondary" onClick={handleEstimatesCsvExport}>
                    Herunterladen
                  </Button>
                </div>

                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white">Kundenstammdaten</p>
                    <p className="text-sm text-white/50">Komplette Kundenliste mit Adressen</p>
                  </div>
                  <Button variant="secondary" onClick={handleCustomersCsvExport}>
                    Herunterladen
                  </Button>
                </div>
              </div>
            </GlassPanel>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Hinweise</h2>
              <div className="space-y-3 text-sm text-white/60">
                <p>
                  <strong className="text-white">DATEV-Format:</strong> Kompatibel mit DATEV Unternehmen Online und den meisten deutschen Buchhaltungssystemen.
                </p>
                <p>
                  <strong className="text-white">CSV-Format:</strong> Semikolon-getrennt für deutsche Excel-Versionen.
                </p>
                <p>
                  <strong className="text-white">Kodierung:</strong> Alle Exporte verwenden UTF-8 Kodierung.
                </p>
              </div>
            </GlassPanel>

            {/* GDPR Info */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">DSGVO</h2>
              <p className="text-sm text-white/60">
                Sie können alle Daten eines Kunden für DSGVO-Datenportabilität exportieren.
                Öffnen Sie dazu die Kundendetailseite und wählen Sie "Daten exportieren".
              </p>
            </GlassPanel>
          </div>
        </div>
      </AppShell>
    </ProtectedRoute>
  );
}
